import { useCallback, useEffect, useState } from "react";
import { Alert, Button, Form, Select, Spin } from "antd";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { TypeParams } from "../../../types";
import { makeApiRequest } from "../../../lib/api";
import { WAITLIST } from "../../../lib/lib";
import { useTranslation } from "react-i18next"; // Import useTranslation hook
const getSchema = (): string => "public";

const showOpenAppointmentsOnly = false;

const formatDate = (timestamp: number, formatStr: string): string => {
  if (!timestamp) {
    return "N/A";
  }
  return format(new Date(timestamp * 1000), formatStr, { locale: de });
};

const { Option } = Select;

interface Form2DatesProps {
  commitRegistration: (dateId: string | number | null, response: any) => void;
  returnBackToPlaces: () => void;
  params: TypeParams; // Define the type of the ccid prop
  placeId: number;
}

const Form2Dates: React.FC<Form2DatesProps> = ({ commitRegistration, returnBackToPlaces, placeId, params }) => {
  const { t } = useTranslation(); // Initialize useTranslation hook
  const [error, setError] = useState("");
  const [form] = Form.useForm();
  const [dates, setDates] = useState<null | any[]>(null);
  const [place, setPlace] = useState<null | any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentDateId, setCurrentDateId] = useState<string | number | null>(null);

  // FORM 1 SUBMIT: send email or phone number to validate
  const onLoadForm = useCallback(async () => {
    setError(""); // Clear previous errors

    try {
      const action = "REACT_GET_AFTERCARE_DATES";
      const content = { place_id: placeId };
      const response = (await makeApiRequest(params, action, content)) as any;
      const parsed: any = JSON.parse(response);
      // console.log(parsed);

      if (!parsed || parsed.STATUS !== "OK" || !parsed.CONTENT) {
        setError(t("kontrolltermin.dates.error"));
      } else {
        setDates(parsed.CONTENT.DATES);
        setPlace(parsed.CONTENT.PLACE);
      }
    } catch (error) {
      // console.log("error", error);
      setError(t("kontrolltermin.dates.error"));
    }
  }, [placeId, params, t]);

  const onFinishFailed = async () => {
    setError(t("kontrolltermin.dates.formIncomplete")); // Clear previous errors
  };

  const onFinishForm = async () => {
    setError(""); // Clear previous errors
    setIsSubmitting(true); // Set loading state

    const dateId = form.getFieldValue("date_id");

    if (!dateId) {
      setError(t("anpassung.dates.noDateSelected"));
      setIsSubmitting(false); // Reset loading state
      return;
    }

    // GET ANPASSUNG PLACE
    let action = "";
    let content: any = {
      place_id: placeId,
    };

    // DEFAULT REGISTRATION
    action = "REACT_AFTERCARE_REGISTRATION";
    content.ws_id = dateId;

    try {
      const responsePlace: string = (await makeApiRequest(params, action, content)) as any;
      const parsed = JSON.parse(responsePlace);
      if (!parsed || parsed.STATUS !== "OK") {
        setError(t("anpassung.registration.errorContactSupport"));
        return;
      }

      commitRegistration(dateId, parsed);
    } catch (error) {
      setError(t("anpassung.registration.genericError"));
    } finally {
      setIsSubmitting(false); // Reset loading state
    }
  };

  const submitToWaitlist = async () => {
    setError(""); // Clear previous errors
    setIsSubmitting(true); // Set loading state

    // GET ANPASSUNG PLACE
    let action = "";
    let content: any = {
      place_id: placeId,
    };

    // DEFAULT REGISTRATION
    action = "REACT_AFTERCARE_WAITLIST";

    try {
      const responsePlace: string = (await makeApiRequest(params, action, content)) as any;
      const parsed = JSON.parse(responsePlace);
      if (!parsed || parsed.STATUS !== "OK") {
        setError(t("anpassung.registration.errorContactSupport"));
        return;
      }

      commitRegistration(WAITLIST, parsed);
    } catch (error) {
      setError(t("anpassung.registration.genericError"));
    } finally {
      setIsSubmitting(false); // Reset loading state
    }
  };

  // INIT
  useEffect(() => {
    onLoadForm();
  }, [onLoadForm]);

  if (!dates || !place) {
    return (
      <div className={"my-lg"}>
        <Spin />
      </div>
    );
  }

  const options: any[] = [];
  let optionHeader: string = t("kontrolltermin.dates.noDatesFound");
  if (dates && dates.length > 0) {
    optionHeader = t("kontrolltermin.dates.chooseDate");
    dates.forEach((ws: any) => {
      // If you need to include condition with `day_to_wait`
      // if (day_to_wait < ws.ws_timestamp) {
      let fullname = formatDate(ws.ws_timestamp, "dd. MMM. yyyy - HH:mm");
      if (ws.ws_completed) {
        if (showOpenAppointmentsOnly) {
          return; // continue to next iteration
        }
        fullname += " - " + t("kontrolltermin.dates.fullyBooked");
        options.push({
          id: fullname,
          name: fullname,
          disabled: true,
        });
      } else {
        options.push({
          id: `${getSchema()}_${ws.id}`,
          name: fullname,
        });
        // [`${getSchema()}_${ws.id}`] = fullname;
      }
      // }
    });
    options.push({ id: `${getSchema()}_${WAITLIST}`, name: t("kontrolltermin.dates.waitingList") });
  }

  return (
    <>
      <p className={"my-md"}>
        <strong>{t("kontrolltermin.dates.title")}</strong>
      </p>

      {error && <Alert message={error} />}

      {options && options.length > 0 && (
        <>
          <p>
            {t("kontrolltermin.dates.availableDates", {
              placeName: place["name"],
              placeSecondName: place["second_name"] ? `(${place["second_name"]})` : "",
            })}
          </p>
          <br />
          <p>{t("kontrolltermin.dates.contactUs")}</p>
          <br />
        </>
      )}

      {options && options.length === 0 && (
        <>
          <p>
            {t("kontrolltermin.dates.noAvailableDates", {
              placeName: place["name"],
              placeSecondName: place["second_name"] ? `(${place["second_name"]})` : "",
            })}
          </p>
        </>
      )}

      <div className={"my-lg"}>
        <Form
          name="basic"
          form={form}
          wrapperCol={{ span: 16 }}
          onFinish={onFinishForm}
          onFinishFailed={onFinishFailed}
          style={{ maxWidth: 600 }}
          autoComplete="off"
        >
          {options && options.length > 0 && (
            <>
              <Form.Item
                label={t("kontrolltermin.dates.selectDate")}
                name="date_id"
                rules={[{ required: true, message: t("kontrolltermin.dates.selectDate") }]}
              >
                <Select
                  placeholder={t("kontrolltermin.dates.selectPlaceholder")}
                  onChange={(val: string) => setCurrentDateId(val)}
                  loading={options === null || options === undefined}
                >
                  {optionHeader}
                  {options.map((date: any) => (
                    <Option key={date.id} value={date.id} disabled={date.disabled}>
                      {date.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </>
          )}
          <Form.Item wrapperCol={{ offset: 12, span: 16 }}>
            <Button type="link" onClick={returnBackToPlaces}>
              {t("kontrolltermin.dates.back")}
            </Button>
            {(dates.length === 0 || currentDateId === "public_" + WAITLIST) && (
              <Button type="primary" onClick={submitToWaitlist} loading={isSubmitting}>
                {t("kontrolltermin.dates.waitingListButton")}
              </Button>
            )}
            &nbsp;
            {options && options.length > 0 && currentDateId !== "public_" + WAITLIST && (
              <Button type="primary" htmlType="submit" loading={isSubmitting}>
                {t("kontrolltermin.dates.submit")}
              </Button>
            )}
          </Form.Item>
        </Form>
      </div>
      {/* <div>* Pflichtfelder</div> */}
    </>
  );
};

export default Form2Dates;
